import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Loader2, Search, Filter, ChevronLeft, ChevronRight,
  Eye, Calendar, Tag, Globe, ArrowRight, Zap, Newspaper,
  BookOpen, ExternalLink
} from 'lucide-react';
import { apiGet } from '../lib/apiClient';

const CATEGORY_LABELS = {
  ieepa: 'IEEPA',
  tariff_updates: 'Tariff Updates',
  commodity_codes: 'Commodity Codes',
  trade_policy: 'Trade Policy',
  fta_agreements: 'FTA Agreements',
  section_301: 'Section 301',
  customs_regulations: 'Customs Regulations',
  global_trade: 'Global Trade',
};

const CATEGORY_COLORS = {
  ieepa: 'bg-red-50 text-red-700',
  tariff_updates: 'bg-blue-50 text-blue-700',
  commodity_codes: 'bg-emerald-50 text-emerald-700',
  trade_policy: 'bg-violet-50 text-violet-700',
  fta_agreements: 'bg-amber-50 text-amber-700',
  section_301: 'bg-orange-50 text-orange-700',
  customs_regulations: 'bg-teal-50 text-teal-700',
  global_trade: 'bg-indigo-50 text-indigo-700',
};

const Resources = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [articles, setArticles] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [preLaunchMode, setPreLaunchMode] = useState(false);

  useEffect(() => {
    apiGet('/api/pre-launch/status', { auth: false })
      .then(d => { if (d) setPreLaunchMode(!!d.pre_launch_mode); })
      .catch(() => {});
  }, []);

  const currentPage = parseInt(searchParams.get('page') || '1');
  const currentCategory = searchParams.get('category') || '';
  const currentRegion = searchParams.get('region') || '';

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', currentPage);
      params.set('limit', '12');
      if (currentCategory) params.set('category', currentCategory);
      if (currentRegion) params.set('region', currentRegion);
      if (searchQuery) params.set('search', searchQuery);

      const data = await apiGet(`/api/news/articles?${params}`, { auth: false });
      setArticles(data?.articles || []);
      setTotal(data?.total || 0);
      setPages(data?.pages || 1);
    } catch {}
    finally { setLoading(false); }
  }, [currentPage, currentCategory, currentRegion, searchQuery]);

  const fetchCategories = useCallback(async () => {
    try {
      const data = await apiGet('/api/news/categories', { auth: false });
      setCategories(data?.categories || []);
    } catch {}
  }, []);

  useEffect(() => { fetchCategories(); }, []);
  useEffect(() => { fetchArticles(); }, [fetchArticles]);

  const viewArticle = async (articleId) => {
    try {
      const article = await apiGet(`/api/news/articles/${articleId}`, { auth: false });
      if (article) setSelectedArticle(article);
    } catch {}
  };

  const setFilter = (key, value) => {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value);
    else params.delete(key);
    params.set('page', '1');
    setSearchParams(params);
  };

  const setPage = (p) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', p.toString());
    setSearchParams(params);
  };

  // Single article view
  if (selectedArticle) {
    return (
      <div className="min-h-screen bg-white">
        {/* Nav */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-100">
          <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2" data-testid="article-nav-logo">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#2563EB] to-[#06B6D4] flex items-center justify-center">
                <Zap className="w-4 h-4 text-white" />
              </div>
              <span className="font-outfit font-bold text-lg text-slate-900">Decoded</span>
            </Link>
            <button onClick={() => setSelectedArticle(null)} className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1" data-testid="article-back-btn">
              <ChevronLeft className="w-4 h-4" /> Back to Resources
            </button>
          </div>
        </nav>

        <main className="pt-24 pb-16 px-6">
          <article className="max-w-3xl mx-auto" data-testid="article-detail">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${CATEGORY_COLORS[selectedArticle.category] || 'bg-slate-100 text-slate-600'}`}>
                  {CATEGORY_LABELS[selectedArticle.category] || selectedArticle.category}
                </span>
                <span className="text-xs text-slate-400">{selectedArticle.region}</span>
                {selectedArticle.confidence && (
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                    selectedArticle.confidence === 'high' ? 'bg-emerald-50 text-emerald-600' :
                    selectedArticle.confidence === 'medium' ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-red-600'
                  }`}>
                    {selectedArticle.confidence} confidence
                  </span>
                )}
              </div>
              <h1 className="font-outfit text-3xl sm:text-4xl font-bold text-slate-900 mb-3" data-testid="article-title">
                {selectedArticle.title}
              </h1>
              <p className="text-lg text-slate-600 mb-4">{selectedArticle.summary}</p>
              <div className="flex items-center gap-4 text-sm text-slate-400">
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {selectedArticle.published_at ? new Date(selectedArticle.published_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Draft'}
                </span>
                <span className="flex items-center gap-1">
                  <Eye className="w-4 h-4" /> {selectedArticle.views || 0} views
                </span>
              </div>
            </div>

            <div className="prose prose-slate max-w-none text-slate-700 leading-relaxed whitespace-pre-line" data-testid="article-content">
              {selectedArticle.content}
            </div>

            {selectedArticle.tags?.length > 0 && (
              <div className="mt-8 pt-6 border-t border-slate-100">
                <div className="flex items-center gap-2 flex-wrap">
                  <Tag className="w-4 h-4 text-slate-400" />
                  {selectedArticle.tags.map((tag, i) => (
                    <span key={i} className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">{tag}</span>
                  ))}
                </div>
              </div>
            )}

            {selectedArticle.source_references?.length > 0 && (
              <div className="mt-6 pt-6 border-t border-slate-100">
                <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                  <BookOpen className="w-4 h-4" /> Sources
                </h4>
                <ul className="space-y-1">
                  {selectedArticle.source_references.map((src, i) => (
                    <li key={i} className="text-sm text-slate-500 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-300 flex-shrink-0" />
                      {src}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </article>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="resources-nav-logo">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#2563EB] to-[#06B6D4] flex items-center justify-center">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="font-outfit font-bold text-lg text-slate-900">Decoded</span>
          </Link>
          <div className="flex items-center gap-4">
            {preLaunchMode ? (
              <>
                <a href="/#features" className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors" data-testid="resources-nav-features">Features</a>
                <Link to="/resources" className="text-sm font-medium text-blue-600">Resources</Link>
                <a href="/#waitlist" data-testid="resources-nav-join-waitlist">
                  <Button size="sm" className="bg-[#0B3D91] hover:bg-[#082A66]">Join Waitlist</Button>
                </a>
              </>
            ) : (
              <>
                <Link to="/resources" className="text-sm font-medium text-blue-600">Resources</Link>
                <Link to="/login" data-testid="resources-nav-signin"><Button size="sm" variant="outline">Sign In</Button></Link>
                <Link to="/signup" data-testid="resources-nav-getstarted"><Button size="sm" className="bg-[#0B3D91] hover:bg-[#082A66]">Get Started</Button></Link>
              </>
            )}
          </div>
        </div>
      </nav>

      <main className="pt-24 pb-16 px-6" data-testid="resources-page">
        <div className="max-w-7xl mx-auto">
          {/* Hero */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <Newspaper className="w-4 h-4" />
              Trade Intelligence
            </div>
            <h1 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4" data-testid="resources-title">
              Resources & News
            </h1>
            <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
              Stay informed with the latest tariff updates, regulatory changes, and trade policy news from around the world.
            </p>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3 mb-8">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchArticles()}
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                data-testid="resources-search-input"
              />
            </div>

            {/* Category filter */}
            <select
              value={currentCategory}
              onChange={e => setFilter('category', e.target.value)}
              className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              data-testid="resources-category-filter"
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{CATEGORY_LABELS[cat] || cat}</option>
              ))}
            </select>

            {/* Region filter */}
            <select
              value={currentRegion}
              onChange={e => setFilter('region', e.target.value)}
              className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              data-testid="resources-region-filter"
            >
              <option value="">All Regions</option>
              <option value="US">United States</option>
              <option value="EU">European Union</option>
              <option value="UK">United Kingdom</option>
              <option value="Global">Global</option>
            </select>

            <Button size="sm" variant="outline" onClick={fetchArticles} data-testid="resources-search-btn">
              <Search className="w-4 h-4 mr-1" /> Search
            </Button>
          </div>

          {/* Articles Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
            </div>
          ) : articles.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-2xl shadow-sm border border-slate-100">
              <BookOpen className="w-16 h-16 text-slate-200 mx-auto mb-4" />
              <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-2">No articles yet</h3>
              <p className="text-slate-500 max-w-md mx-auto">
                Check back soon for the latest trade and tariff news, or try a different search.
              </p>
            </div>
          ) : (
            <>
              <p className="text-sm text-slate-500 mb-4">{total} article{total !== 1 ? 's' : ''} found</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="articles-grid">
                {articles.map(article => (
                  <div
                    key={article.article_id}
                    className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden hover:shadow-md hover:border-blue-200 transition-all cursor-pointer group"
                    onClick={() => viewArticle(article.article_id)}
                    data-testid={`article-card-${article.article_id}`}
                  >
                    <div className="p-6">
                      <div className="flex items-center gap-2 mb-3">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${CATEGORY_COLORS[article.category] || 'bg-slate-100 text-slate-600'}`}>
                          {CATEGORY_LABELS[article.category] || article.category}
                        </span>
                        <span className="text-xs text-slate-400 flex items-center gap-1">
                          <Globe className="w-3 h-3" /> {article.region}
                        </span>
                      </div>
                      <h3 className="font-outfit text-lg font-semibold text-slate-900 mb-2 group-hover:text-blue-700 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-sm text-slate-500 mb-4 line-clamp-3">
                        {article.summary}
                      </p>
                      <div className="flex items-center justify-between text-xs text-slate-400">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {article.published_at ? new Date(article.published_at).toLocaleDateString() : 'Pending'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Eye className="w-3 h-3" /> {article.views || 0}
                        </span>
                      </div>
                      {article.tags?.length > 0 && (
                        <div className="flex gap-1 mt-3 flex-wrap">
                          {article.tags.slice(0, 3).map((tag, i) => (
                            <span key={i} className="text-xs bg-slate-50 text-slate-500 px-2 py-0.5 rounded-full">{tag}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {pages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-10">
                  <Button
                    size="sm" variant="outline" disabled={currentPage <= 1}
                    onClick={() => setPage(currentPage - 1)}
                    data-testid="resources-prev-page"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-slate-600 px-3">Page {currentPage} of {pages}</span>
                  <Button
                    size="sm" variant="outline" disabled={currentPage >= pages}
                    onClick={() => setPage(currentPage + 1)}
                    data-testid="resources-next-page"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 bg-slate-900">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#2563EB] to-[#06B6D4] flex items-center justify-center">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="font-outfit font-bold text-white">Decoded</span>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <Link to="/" className="text-slate-400 hover:text-white transition-colors">Home</Link>
            <Link to="/terms" className="text-slate-400 hover:text-white transition-colors">Terms</Link>
            <Link to="/privacy" className="text-slate-400 hover:text-white transition-colors">Privacy</Link>
          </div>
          <p className="text-slate-500 text-xs">© {new Date().getFullYear()} Decoded. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Resources;
